import { toast } from 'react-hot-toast'
import type { AxiosError, AxiosInstance } from 'axios'

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Set on a call's config to suppress the automatic error popup that
     * installAutoErrorToast below shows, for callers that already display
     * their own specific message on failure.
     */
    skipErrorToast?: boolean
  }
}

/**
 * Turns a failed request into a message worth showing the user. A 403 from
 * this app's routes typically only carries a generic body like
 * `{ message: 'Forbidden' }`, which isn't useful surfaced as-is, so it's
 * replaced with an action-specific one. Handles both axios errors
 * (`err.response.status`) and plain fetch-based errors that attach a
 * `status` property directly (see lib/api-client.ts's fetch fallbacks).
 *
 * `action` is optional: callers that just want the server's own wording pass
 * nothing and get the raw message.
 */
export function getApiErrorMessage(err: any, action?: string): string {
  const status = err?.response?.status ?? err?.status
  if (status === 403) {
    return action
      ? `You don't have permission to ${action}.`
      : "You don't have permission to do this."
  }
  const data = err?.response?.data
  if (data?.details && Array.isArray(data.details) && data.details.length > 0) {
    return data.details.map((d: any) => d.message || d).join('; ')
  }
  if (data?.message) return data.message
  if (err?.message) return err.message
  return action ? `Failed to ${action}.` : 'Something went wrong.'
}

/**
 * The one error-handling rule every API client in this app follows: a 403,
 * a server error, or a request that never got a response at all (network
 * down) otherwise fails completely silently in the UI, so show a popup for
 * those automatically. Validation-style errors (400/404/409/...) are
 * deliberately excluded — those come back with a specific backend message a
 * caller almost always wants to show inline, next to the relevant field,
 * rather than as a generic popup.
 *
 * A caller that already shows its own message on failure (a local
 * `catch { toast.error(getApiErrorMessage(err, 'do the specific thing')) }`)
 * should pass `{ skipErrorToast: true }` in that request's config so the
 * user doesn't see the message twice. New code that doesn't need a more
 * specific message can skip writing its own catch entirely and rely on
 * this.
 */
export function installAutoErrorToast(client: AxiosInstance) {
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status
      const isNetworkError = !error.response
      if (!error.config?.skipErrorToast && (status === 403 || (status && status >= 500) || isNetworkError)) {
        toast.error(
          isNetworkError
            ? 'Could not reach the server. Check your connection and try again.'
            : getApiErrorMessage(error, 'do this')
        )
      }
      return Promise.reject(error)
    }
  )
}
